import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../api";
import { MIGRATION } from "../../../constants/testIds";
import { computeFinalRows } from "../../../lib/migrationParse";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { SubmitButton } from "../../common/SubmitButton";

// SCR-P04-14 - classifies every row clean/flagged/duplicate WITHOUT writing anything. Flagged
// rows can be fixed inline (missing name/phone) and re-previewed as many times as needed.
export function PreviewStep({
  batchId, setBatchId, files, mapping, mappedRows, dedupeDecisions, fieldOverrides, setFieldOverrides,
  previewResult, setPreviewResult, onBack, onContinue,
}) {
  const [fixDrafts, setFixDrafts] = useState({});
  const finalRows = useMemo(() => computeFinalRows(mappedRows, dedupeDecisions, fieldOverrides), [mappedRows, dedupeDecisions, fieldOverrides]);

  const previewMutation = useMutation({
    mutationFn: () => api.post("/migration/preview", {
      source_files: files.length ? files.map((f) => ({ name: f.name, row_count: f.rows.length })) : (previewResult?.source_files || []),
      mapping, rows: finalRows, existing_batch_id: batchId,
    }),
    onSuccess: (r) => { setPreviewResult(r.data); setBatchId(r.data.id); },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Preview failed"),
  });

  const hasRun = !!previewResult?.counts;
  const flaggedRows = previewResult?.flagged_rows || [];

  const applyFix = (rowKey, field, value) => {
    setFieldOverrides((prev) => ({ ...prev, [rowKey]: { ...(prev[rowKey] || {}), [field]: value } }));
  };

  return (
    <div className="space-y-4">
      {!hasRun && (
        <div className="solid-surface rounded-[14px] p-8 text-center">
          <p className="text-sm mb-3" style={{ color: "var(--scarlet-text-secondary)" }}>{finalRows.length} rows ready to classify - nothing will be written yet.</p>
          <SubmitButton testId={MIGRATION.previewReRunButton} pending={previewMutation.isPending} onClick={() => previewMutation.mutate()} label="Run preview" />
        </div>
      )}

      {hasRun && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div data-testid={MIGRATION.previewCleanTile} className="solid-surface rounded-[12px] p-4 text-center">
              <p className="text-2xl font-bold" style={{ color: "#3FA65B" }}>{previewResult.counts.clean}</p>
              <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Clean</p>
            </div>
            <div data-testid={MIGRATION.previewFlaggedTile} className="solid-surface rounded-[12px] p-4 text-center">
              <p className="text-2xl font-bold" style={{ color: "#C9A227" }}>{previewResult.counts.flagged}</p>
              <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Flagged</p>
            </div>
            <div data-testid={MIGRATION.previewDuplicateTile} className="solid-surface rounded-[12px] p-4 text-center">
              <p className="text-2xl font-bold" style={{ color: "#8B8B92" }}>{previewResult.counts.duplicates}</p>
              <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Duplicates</p>
            </div>
          </div>

          {flaggedRows.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold" style={{ color: "var(--scarlet-text)" }}>Flagged rows{files.length > 0 ? " - fix inline, then re-run preview" : ""}</p>
              {files.length === 0 && (
                <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Re-upload the original file(s) in a new migration to fix these inline - this resumed view is read-only.</p>
              )}
              {flaggedRows.map((row) => (
                <div key={row.row_key} data-testid={MIGRATION.previewFlaggedRow(row.row_key)} className="solid-surface rounded-[10px] p-3 flex items-center gap-3 flex-wrap">
                  <span className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{row.source_file}</span>
                  {row.missing_fields.map((field) => (
                    <Input
                      key={field} data-testid={MIGRATION.previewFlaggedFixInput(row.row_key, field)}
                      placeholder={`Missing: ${field}`} className="w-40" defaultValue={row.data[field] || ""} disabled={files.length === 0}
                      onChange={(e) => applyFix(row.row_key, field, e.target.value)}
                    />
                  ))}
                </div>
              ))}
              {files.length > 0 && (
                <Button data-testid={MIGRATION.previewReRunButton} size="sm" variant="outline" onClick={() => previewMutation.mutate()}>
                  <RefreshCw size={14} className="mr-1.5" /> Re-run preview
                </Button>
              )}
            </div>
          )}
        </>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>Back</Button>
        <Button data-testid={MIGRATION.wizardContinueButton} disabled={!hasRun} onClick={onContinue} style={{ background: "var(--scarlet-accent)", color: "#fff" }}>
          Continue to Commit
        </Button>
      </div>
    </div>
  );
}
